import { groupBy, keyBy, mapValues, omit, difference } from 'lodash';
import { isDefined, unreachable } from '../../../../ts-utils';
import { constructScrollAnchor, } from '../comments-types';
import { getPrevReplyChunkState } from '../paging/comments-paging-selectors';
import { I_AM_TYPING, LIVE_COMMENTS_RECEIVED, REVEAL_LIVE_COMMENTS, REVEAL_LIVE_REPLIES, SOMEONE_IS_TYPING, FETCH_LIVE_COMMENTS_SUCCESS, FETCH_LIVE_COMMENTS_FAILURE, FETCH_LIVE_COMMENTS_NOT_FOUND, } from './live-updates-actions';
import { getReplyListState } from '../replies/replies-selectors';
import { makeCommentInnerListId } from '../../../../component/components/comment-list/id-utils';
import { getVotesStateIN } from '../votes/comments-vote-utils';
import { hiddenComponentEffectsGuard } from '../effects/hidden-component-effects-guard';
import { ensureRicosContent } from '@wix/ricos';
import { getMessageFromError } from '../comments-errors';
export function liveUpdatesReducer(state, action) {
    switch (action.type) {
        case LIVE_COMMENTS_RECEIVED: {
            const groupedUpdates = groupBy(action.payload, (params) => params.resourceId);
            return mapValues(state, (resourceState, resourceId) => {
                if (resourceState?.type !== 'READY') {
                    return resourceState;
                }
                const updatesForResource = groupedUpdates[resourceId];
                if (!updatesForResource || updatesForResource.length === 0) {
                    return resourceState;
                }
                return updatesForResource.reduce((updatedResourceState, liveUpdate) => {
                    if (liveUpdate.type === 'TOP_LEVEL_COMMENT') {
                        return liveTopLevelCommentReceived(updatedResourceState, liveUpdate);
                    }
                    else {
                        return liveReplyReceived(updatedResourceState, liveUpdate);
                    }
                }, resourceState);
            });
        }
        case REVEAL_LIVE_COMMENTS: {
            const { position, resourceId } = action.payload;
            const resourceState = state[resourceId];
            if (resourceState?.type !== 'READY') {
                return state;
            }
            const newState = {
                ...resourceState,
                topLevelCommentList: {
                    ...resourceState.topLevelCommentList,
                    liveCommentsPurgatory: [],
                    chunks: position === 'start'
                        ? [
                            {
                                type: 'LIVE',
                                commentIds: resourceState.topLevelCommentList.liveCommentsPurgatory,
                            },
                            ...resourceState.topLevelCommentList.chunks,
                        ]
                        : [
                            ...resourceState.topLevelCommentList.chunks,
                            {
                                type: 'LIVE',
                                commentIds: resourceState.topLevelCommentList.liveCommentsPurgatory,
                            },
                        ],
                },
                ...hiddenComponentEffectsGuard(resourceState, {
                    scrollAnchor: position === 'end'
                        ? // Top button will disappear too on this click, so we need to anchor all list on this action
                            constructScrollAnchor(makeCommentInnerListId(resourceId))
                        : undefined,
                }),
            };
            return {
                ...state,
                [resourceId]: newState,
            };
        }
        case REVEAL_LIVE_REPLIES: {
            const { parentId, resourceId } = action.payload;
            const resourceState = state[resourceId];
            if (resourceState?.type !== 'READY') {
                return state;
            }
            const replyListState = resourceState.replyLists[parentId];
            if (replyListState.liveRepliesPurgatory.length === 0) {
                return state;
            }
            if (getPrevReplyChunkState(replyListState).type !== 'NONE') {
                // We don't show live replies if there are 'show newer replies' link, as they fall under it
                return state;
            }
            const newState = {
                ...resourceState,
                replyLists: {
                    ...resourceState.replyLists,
                    [parentId]: {
                        ...replyListState,
                        liveRepliesPurgatory: [],
                        chunks: [
                            { type: 'LIVE', commentIds: replyListState.liveRepliesPurgatory },
                            ...replyListState.chunks,
                        ],
                    },
                },
            };
            return {
                ...state,
                [resourceId]: newState,
            };
        }
        case FETCH_LIVE_COMMENTS_SUCCESS: {
            const { comments, votes } = action.payload;
            const commentsByResource = groupBy(comments, (comment) => comment.resourceId);
            return mapValues(state, (resourceState, resourceId) => {
                if (resourceState?.type !== 'READY') {
                    return resourceState;
                }
                const commentsForResource = commentsByResource[resourceId];
                if (!commentsForResource) {
                    return resourceState;
                }
                return replacePendingLiveCommentsWithFetched(resourceState, commentsForResource, votes);
            });
        }
        case FETCH_LIVE_COMMENTS_NOT_FOUND: {
            const { commentIds } = action.payload;
            return mapValues(state, (resourceState) => {
                if (resourceState?.type !== 'READY') {
                    return resourceState;
                }
                const commentsForResource = commentIds.filter((id) => !!resourceState.commentsById[id]);
                if (commentsForResource.length < 0) {
                    return resourceState;
                }
                return makeNotFoundCommentsToAppearAsDeleted(resourceState, commentsForResource);
            });
        }
        case FETCH_LIVE_COMMENTS_FAILURE: {
            const { commentIds } = action.payload;
            // We want to show toats only from one resource. Review after error state refactoring to single instance instead of per resource
            let errorSubmitted = false;
            return mapValues(state, (resourceState) => {
                if (resourceState?.type !== 'READY') {
                    return resourceState;
                }
                const commentsForResource = commentIds.filter((id) => !!resourceState.commentsById[id]);
                if (commentsForResource.length < 0) {
                    return resourceState;
                }
                const error = errorSubmitted ? { type: 'SILENT' } : action.payload.error;
                errorSubmitted = true;
                return removeFailedComments(resourceState, commentsForResource, getMessageFromError(error));
            });
        }
        case I_AM_TYPING: {
            // handled by middleware
            return state;
        }
        case SOMEONE_IS_TYPING: {
            const { resourceId, subaction } = action.payload;
            const resourceState = state[resourceId];
            if (resourceState?.type !== 'READY') {
                return state;
            }
            return {
                ...state,
                [resourceId]: {
                    ...resourceState,
                    ...hiddenComponentEffectsGuard(resourceState, {
                        ...resourceState.effects,
                        someoneIsTyping: subaction,
                    }),
                },
            };
        }
        default: {
            unreachable(action);
            return state;
        }
    }
}
function liveTopLevelCommentReceived(state, params) {
    const { commentId, commentEntity, createdBy: author } = params;
    if (state.commentsById[commentId]) {
        return state;
    }
    const firstLiveComment = state.topLevelCommentList.liveCommentsPurgatory.length === 0;
    return {
        ...state,
        commentsById: {
            ...state.commentsById,
            [commentId]: {
                type: 'COMMENT_LIVE_PENDING',
                commentId,
                content: commentEntityContentWithRichContent(commentEntity),
                author: author,
            },
        },
        topLevelCommentList: {
            ...state.topLevelCommentList,
            liveCommentsPurgatory: [commentId, ...state.topLevelCommentList.liveCommentsPurgatory],
        },
        replyLists: {
            ...state.replyLists,
            [commentId]: {
                parentCommentId: commentId,
                newRepliesToReplies: {},
                nextChunkState: { type: 'IDLE' },
                prevChunkState: { type: 'IDLE' },
                liveRepliesPurgatory: [],
                chunks: [],
            },
        },
        ...hiddenComponentEffectsGuard(state, 
        // Silence 'someone is typing' on the live comments button appearance
        firstLiveComment && state.effects?.someoneIsTyping === 'started'
            ? {
                someoneIsTyping: 'ended',
            }
            : {}),
        totals: state.totals + 1,
    };
}
function liveReplyReceived(state, params) {
    const { commentId, commentEntity, createdBy: author, replyTo, topLevelParentId } = params;
    if (state.commentsById[commentId]) {
        return state;
    }
    if (!state.commentsById[topLevelParentId]) {
        // Don't keep replies for unknown top level comments
        return state;
    }
    const replyStateOut = getReplyListState(state.replyLists[topLevelParentId], state.commentsById);
    if (replyStateOut?.type === 'FULL' && replyStateOut.prev.type !== 'NONE') {
        // We have 'view newer' button here at the bottom. It's rare and complicated situation,
        // so we skip live replies for it
        return state;
    }
    return {
        ...state,
        commentsById: {
            ...state.commentsById,
            [commentId]: {
                type: 'REPLY_LIVE_PENDING',
                commentId,
                targetId: replyTo?.commentId,
                targetStatus: replyTo.status,
                parentId: topLevelParentId,
                content: commentEntityContentWithRichContent(commentEntity),
                author: author,
            },
        },
        replyLists: {
            ...state.replyLists,
            [topLevelParentId]: {
                ...state.replyLists[topLevelParentId],
                liveRepliesPurgatory: [
                    commentId,
                    ...state.replyLists[topLevelParentId].liveRepliesPurgatory,
                ],
            },
        },
        totals: state.totals + 1,
    };
}
function replacePendingLiveCommentsWithFetched(state, comments, votes) {
    const fetchedComments = keyBy(comments
        .map((c) => {
        const corresponding = state.commentsById[c.commentId];
        if (corresponding.type === 'REPLY_LIVE_PENDING') {
            return {
                type: 'REPLY_READY',
                isLive: true,
                comment: c,
                commentId: c.commentId,
                reactions: { total: 0, reactions: [], type: 'IDLE' },
                votes: { type: 'IDLE' },
                targetId: c.replyTo?.commentId,
                targetStatus: c.replyTo?.status,
                parentId: corresponding.parentId,
                replyingToTargetLoading: false,
                marked: { type: 'IDLE' },
            };
        }
        if (corresponding.type === 'COMMENT_LIVE_PENDING') {
            return {
                type: 'COMMENT_READY',
                isLive: true,
                comment: c,
                commentId: c.commentId,
                reactions: { total: 0, reactions: [], type: 'IDLE' },
                marked: { type: 'IDLE' },
                votes: getVotesStateIN(c, votes),
            };
        }
        return undefined;
    })
        .filter(isDefined), (c) => c.commentId);
    return {
        ...state,
        commentsById: {
            ...state.commentsById,
            ...fetchedComments,
        },
    };
}
function makeNotFoundCommentsToAppearAsDeleted(state, notFoundIds) {
    return {
        ...state,
        commentsById: mapValues(state.commentsById, (commentState, commentId) => {
            if (!notFoundIds.includes(commentId)) {
                return commentState;
            }
            if (commentState.type === 'COMMENT_LIVE_PENDING') {
                return {
                    type: 'COMMENT_DELETED',
                    commentId,
                };
            }
            if (commentState.type === 'REPLY_LIVE_PENDING') {
                return {
                    type: 'REPLY_DELETED',
                    commentId,
                    parentId: commentState.parentId,
                    targetId: commentState.targetId,
                    targetStatus: commentState.targetStatus,
                    replyingToTargetLoading: false,
                };
            }
            return commentState;
        }),
    };
}
function removeFailedComments(state, idsToRemove, message) {
    // TODO: filter from replies and live comment purgatories
    const cleanChunks = (chunks) => {
        return chunks
            .map((c) => {
            if (c.type !== 'LIVE') {
                return c;
            }
            const filteredIds = difference(c.commentIds, idsToRemove);
            if (filteredIds.length > 0) {
                return { ...c, commentIds: filteredIds };
            }
            else {
                return undefined;
            }
        })
            .filter(isDefined);
    };
    return {
        ...state,
        commentsById: omit(state.commentsById, idsToRemove),
        topLevelCommentList: {
            ...state.topLevelCommentList,
            liveCommentsPurgatory: difference(state.topLevelCommentList.liveCommentsPurgatory, idsToRemove),
            chunks: cleanChunks(state.topLevelCommentList.chunks),
        },
        replyLists: mapValues(state.replyLists, (replyList) => {
            return {
                ...replyList,
                liveRepliesPurgatory: difference(replyList.liveRepliesPurgatory, idsToRemove),
                chunks: cleanChunks(replyList.chunks),
            };
        }),
        ...hiddenComponentEffectsGuard(state, {
            toast: message.type === 'TOAST'
                ? {
                    skin: 'error',
                    message: message.message,
                    timestamp: Date.now(),
                }
                : state.effects?.toast,
        }),
    };
}
const commentEntityContentWithRichContent = (comment) => {
    return comment.type === 'OK'
        ? comment.entity.text
            ? ensureRicosContent(JSON.parse(comment.entity.text.content), {
                ignoreUnsupportedValues: true,
            })
            : undefined
        : undefined;
};
//# sourceMappingURL=live-updates-reducer.js.map